/**
 * Centralise les regles d'acces studio video par plan.
 *
 * Source de verite serveur :
 *   backend/app/routes/video_moments.py — _check_video_studio_access
 *                                       + _check_extraction_quota
 *
 * Plans autorises sur le studio (lecture + creation de moments) :
 *   COACH, CLUB, CLUB_PRO + superadmin
 *
 * Plan FREE : limite cumulative de 5 clips MP4 extraits sur la duree de
 *   l'essai (14 jours). Au-dela, le bouton "Extraire" est desactive avec
 *   CTA Coach+, mais les clips
 *   existants restent lisibles. Compteur stocke en DB sur
 *   users.free_clips_extracted_total et expose par /api/moments/stats/.
 *
 * Tous les autres plans : extraction illimitee.
 */

export const STUDIO_ALLOWED_PLANS  = ['COACH', 'CLUB', 'CLUB_PRO']
export const ANALYSES_ALLOWED_PLANS = ['COACH', 'CLUB', 'CLUB_PRO']
export const FREE_CLIPS_LIMIT = 5

/**
 * L'utilisateur est-il couvert par l'abonnement de son club ?
 *
 * UN PLAN PERSONNEL « FREE » NE DIT PAS QUI PAIE. Un coach membre de club
 * porte un plan personnel FREE tout en consommant le quota de son directeur
 * sportif. Lire `user.plan` seul revient a traiter un client payant comme un
 * compte d'essai · c'est ce qui a masque le bouton Studio et devie le bouton
 * Analyser vers une page d'abonnement. Cas reel · Gazelec Ajaccio, 06/08/2026.
 *
 * PORTAGE EXACT de `get_billing_user` (backend/app/services/quota.py:114), qui
 * exige les TROIS signes. Un abonnement Stripe personnel coupe la resolution :
 * ce compte paie pour lui-meme. Une categorie geree absente aussi : sans elle,
 * le backend ne remonte pas au DS.
 *
 * L'autorite reste le serveur — cette fonction ne fait que guider l'affichage.
 *
 * @param {object|null} user
 * @returns {boolean}
 */
export function estMembreDeClub(user) {
  return !!(user?.club_id && !user?.stripe_subscription_id && user?.managed_category)
}

/**
 * L'utilisateur est-il un VRAI compte d'essai ?
 *
 * Un plan personnel « FREE » ne suffit pas a le dire · un coach membre de club
 * l'affiche aussi, tout en etant couvert par l'abonnement de son club. Toute
 * la difference entre « ce compte doit voir une offre » et « ce compte est un
 * client payant » tient dans cette nuance, et elle etait ecrite en dur, a
 * l'identique et sans le correctif, dans une douzaine d'ecrans.
 *
 * NE TRAITE PAS le superadmin · les appelants qui l'excluent le font deja de
 * leur cote, et l'ajouter ici changerait leur comportement sans qu'on l'ait
 * demande. Ecart connu, releve dans l'audit MEMBRE-CLUB-002.
 *
 * @param {object|null} user
 * @returns {boolean}
 */
export function estVraiFree(user) {
  return (user?.plan || '').toUpperCase() === 'FREE' && !estMembreDeClub(user)
}

/**
 * L'utilisateur a-t-il acces aux fonctions IA (upload video + analyse) ?
 *
 * Quatrieme lecture du meme fait dans cette base, et la troisieme a s'etre
 * trompee · le wizard de creation de match tenait sa propre liste de plans en
 * dur et affichait « LANCE L'ANALYSE AVEC COACH+ » a des coachs dont le club
 * paie deja. Elle passe par le detecteur commun, comme hasStudioAccess et
 * handleLancerAnalyse.
 *
 * Ne traite PAS le superadmin · l'etape 5 ne l'a jamais fait, et le changer
 * silencieusement sortirait du perimetre. Ecart releve dans l'audit du lot.
 *
 * @param {object|null} user
 * @returns {boolean}
 */
export function hasAIPlanAccess(user) {
  if (!user) return false
  if (estMembreDeClub(user)) return true
  return ANALYSES_ALLOWED_PLANS.includes((user.plan || '').toUpperCase())
}

/**
 * Determine si l'user a acces au studio video et, pour FREE, s'il peut
 * encore extraire un nouveau clip.
 *
 * @param {object|null} user - { plan, is_superadmin } depuis useAuth()
 * @param {number} clipsCount - nombre de clips deja extraits par l'user
 *                              (FREE : compteur historique cumulatif depuis
 *                              GET /api/moments/stats/.free_clips_used)
 * @returns {{
 *   allowed: boolean,             // peut acceder au studio (lecture/creation)
 *   canExtract: boolean,          // peut extraire un nouveau clip MP4
 *   reason: 'no_user'|'no_plan'|'free_quota_reached'|null,
 *   quota: {used:number, limit:number}|null  // FREE uniquement, sinon null
 * }}
 */
export function hasStudioAccess(user, clipsCount = 0) {
  if (!user) {
    return { allowed: false, canExtract: false, reason: 'no_user', quota: null }
  }

  if (user.is_superadmin) {
    return { allowed: true, canExtract: true, reason: null, quota: null }
  }

  // Membre de club · couvert par le plan CLUB de son directeur sportif, donc
  // traite comme tel. Sans ce cas, son plan personnel FREE le faisait sortir
  // sur `no_plan` quatre lignes plus bas — et le studio lui etait refuse alors
  // que son club paie. Le backend applique la meme resolution depuis
  // _check_video_studio_access (video_moments.py:67).
  if (estMembreDeClub(user)) {
    return { allowed: true, canExtract: true, reason: null, quota: null }
  }

  const plan = (user.plan || '').toUpperCase()

  if (!STUDIO_ALLOWED_PLANS.includes(plan)) {
    return { allowed: false, canExtract: false, reason: 'no_plan', quota: null }
  }

  if (plan !== 'FREE') {
    return { allowed: true, canExtract: true, reason: null, quota: null }
  }

  // Plan FREE : quota cumulatif sur la duree de l'essai (30 jours)
  const used = Math.max(0, Number(clipsCount) || 0)
  const quota = { used, limit: FREE_CLIPS_LIMIT }

  if (used >= FREE_CLIPS_LIMIT) {
    return {
      allowed: true,                    // peut consulter ses clips existants
      canExtract: false,                // ne peut plus en extraire de nouveaux
      reason: 'free_quota_reached',
      quota,
    }
  }

  return { allowed: true, canExtract: true, reason: null, quota }
}

/**
 * Détermine si l'user peut lancer une analyse IA.
 * La réponse définitive vient du serveur (check_and_consume_quota) ;
 * cette fonction sert uniquement à guider l'affichage côté client.
 *
 * @param {object|null} user - { plan, is_superadmin }
 * @param {number} analysesDisponibles - total dispo (quota_mensuel + reserve)
 */
export function hasAnalysesAccess(user, analysesDisponibles = 0) {
  if (!user) return { allowed: false, reason: 'no_user' }
  if (user.is_superadmin) return { allowed: true, reason: null }
  const plan = (user.plan || '').toUpperCase()
  if (!ANALYSES_ALLOWED_PLANS.includes(plan)) return { allowed: false, reason: 'no_plan' }
  if (analysesDisponibles <= 0) return { allowed: false, reason: 'quota_exhausted' }
  return { allowed: true, reason: null }
}
